import React from "react"

import Layout from "../components/layout"
import Seo from "../components/seo"
import EnglishBreadcrumb from "../utils/EnglishBreadcrumb"

const PrivacyPolicyPage = props => {
  const {
    breadcrumb: { crumbs },
  } = props.pageContext

  return (
    <Layout location={props.location}>
      <Seo
        title="Privacy Policy"
        lang="en-US"
        description="Privacy Policy for Training app"
        pathname="privacy-policy"
      />
      <div className="content">
        <EnglishBreadcrumb
          crumbs={crumbs}
          crumbSeparator=" / "
          crumbLabel={props.location.pathname}
        />
      </div>
      <div className="content privacy-policy">
        <h1>General information</h1>
        <p>
          Emento is glad that you are using Training. It is very important to us
          that we respect and protect your privacy. It is always our intention
          and goal to process your data securely and appropriately.
        </p>
        <p>
          The personal data you provide to us will be treated confidentially and
          will only be disclosed for the purposes we have identified. However,
          it is important that you read these terms and conditions carefully so
          that you understand why and how we process your personal data. If you
          do not find answers to your questions in this document, please feel
          free to contact our Data Protection Officer (DPO) at the following
          email: <a href="mailto:dpo@emento.dk">dpo@emento.dk</a>.
        </p>
        <p>Emento is a Danish company with registered address in Aarhus N:</p>
        <address>
          Emento A/S
          <br />
          CVR no. 37321745
          <br />
          Olof Palmes Allé 44,
          <br />
          DK-8200 Aarhus N
        </address>
        <h2>Who is Emento</h2>
        <p>
          Emento produces and owns the demo tool Training, which illustrates the
          product Emento care guide, which are coherent web and mobile
          applications that help coordinate and navigate programmes.
        </p>
        <p>
          This Privacy Policy describes what information we process when you use
          our Service, how we use that information, and the options we offer you
          to view, update and control that information.
        </p>
        <h2>Who is the data controller</h2>
        <p>
          To access Training, you will be given a username and a password. You
          are basically anonymous and cannot be identified from the information
          contained in Training. Should you pass on information to Emento in
          Training that can identify you, Emento is the data controller.
        </p>
        <h2>What is personal data</h2>
        <p>
          Personal data is any information relating to an identified or
          identifiable natural person. This means that it is any information
          that can be directly or indirectly linked to an individual. It can be,
          for example, name, gender, telephone number or even images and dynamic
          IP addresses.
        </p>
        <p>
          Should we - contrary to expectations - come to process personal data
          about you in connection with your use of Training, we always ensure
          that this is done for a clear and explicit purpose and familiarise you
          with the types of data processed and why the data is processed.
        </p>
        <h2>When does Emento process personal data</h2>
        <p>
          When you use Training, Emento does not generally process personal data
          about you, as your profile is anonymous, but you have the option to
          write your name and insert a profile picture. This is not necessary
          for the use of Training, which is why we recommend that you do not
          provide this information.
        </p>
        <p>
          If you choose not to remain anonymous, we process personal data in the
          following way:
        </p>
        <ul>
          <li>When viewing content in the app</li>
          <li>When you write a message</li>
          <li>When you mark a task as completed</li>
          <li>When you contact our support</li>
        </ul>

        <p>
          When you interact with us through the Training profile, Emento
          processes various types of other information as described below:
        </p>

        <h3>Technical data:</h3>
        <p>
          Emento processes technical data when using the App. Emento also
          processes technical information related to your smartphone (such as:
          IP address, device configuration and country of origin) or related to
          Training (such as: log and history of all data transactions, log and
          history of actions).
        </p>

        <h3>Communication data:</h3>
        <p>
          If you send us a request - for example, an email requesting help - we
          reserve the right to share (without sensitive personal data) this
          information internally in order to clarify and respond to your request
          or to help other users.
        </p>

        <h2>Purpose of use of personal data in Training</h2>
        <p>
          The information we collect about you when you use Training is for the
          following purposes:
        </p>
        <ul>
          <li>
            To improve Training: In order to improve the Guide and ensure
            accessibility to our platform, we keep a record of actions performed
            in a log. We never process your data when it can directly identify
            you unless you have given us consent to do so, e.g. if we need to
            solve a problem that you have brought to our attention.
          </li>
        </ul>
        <p>
          Legal basis for the use of personal data: Emento's processing of data
          for use in a demo programme is based on the legal basis in Article
          6(1)(f) of the General Data Protection Regulation (GDPR).
        </p>
        <h2>Personal data to third parties</h2>
        <p>
          Information can only be disclosed to sub-processors who are authorised
          to use your data for login management and hosting purposes.
        </p>
        <p>
          It is Emento's task to ensure that your personal data is not misused.
          Therefore, we always ensure that our sub-processors guarantee that
          your personal data is protected.
        </p>
        <h2>Storage of personal data in Training</h2>
        <p>
          Emento is committed to protecting your personal data. We use technical
          and organisational measures to safeguard against unauthorised access
          and use, destruction, alteration or disclosure of your personal data
          by any individual or organisation. To ensure your security and
          protection, only a limited number of persons with a legitimate need
          have access to personal data.
        </p>
        <p>
          In order to protect your data in the best possible way, we have
          adopted internal rules on information security, which contain both
          instructions and measures to protect your personal data against
          destruction, loss or alteration, as well as against unauthorised
          disclosure, access or knowledge by unauthorised persons. In addition,
          all Emento employees must follow internal procedures and rules
          regarding the handling of personal data. Training in and around the
          correct handling of personal data is also carried out on an ongoing
          basis.
        </p>
        <h2>Deletion of personal data in Training</h2>
        <p>Your profile and related data will be deleted after 30 days.</p>
        <h2>Your rights</h2>
        <p>
          As a user of Training, you can, on request, find out what data we have
          processed about you, where it comes from and what we use it for. You
          can also find out how long we store your data and who receives data
          about you. However, access may be restricted to protect the privacy,
          trade secrets and intellectual property rights of other persons.
        </p>
        <p>
          If your profile information is incorrect, incomplete or irrelevant,
          you have the right to have it corrected. You can correct the
          information in your profile directly in the app
        </p>
        <p>
          You may at any time lodge a complaint with a supervisory authority
          regarding our processing of your personal data. The Danish supervisory
          authority is Datatilsynet, Carl Jacobsens Vej 35, 2500 Valby. The
          Danish Data Protection Agency can also be contacted by email:{" "}
          <a href="mailto:dt@datatilsynet.dk">dt@datatilsynet.dk</a>.
        </p>
        <h2>Contact information</h2>
        <p>
          If you have any questions or comments about this privacy policy,
          please contact us at:
        </p>
        <address>
          Emento A/S
          <br />
          Olof Palmes Alle 44
          <br />
          DK-8200 Aarhus N<br />
          E-mail: <a href="mailto:dpo@emento.dk">dpo@emento.dk</a>
          <br />
          Phone: +45 72 22 70 70
        </address>
        <p>You also have the possibility to contact our external DPO:</p>
        <address>
          DPO Danmark ApS
          <br />
          Højbro Plads 10
          <br />
          DK-1200 Copenhagen K<br />
          E-mail:{" "}
          <a href="mailto:dpo-emento@dpo-danmark.dk">
            dpo-emento@dpo-danmark.dk
          </a>
          <br />
          Telephone: 77 34 46 00
          <br />
          <a href="https://www.dpo-danmark.dk/dpo-hotline">
            www.dpo-danmark.dk/dpo-hotline
          </a>
        </address>
      </div>
    </Layout>
  )
}

export default PrivacyPolicyPage
